<script setup lang="ts">
    import { onClickOutside, useDebounceFn } from '@vueuse/core';
    import Str from '@supercharge/strings';
    import { FormInputProps, FormInputData } from '~~/composables/useFormInput';
    import ValidateEmailEndpoint, { ValidatedEmailResponse } from '~~/app/common/endpoints/ValidateEmailEndpoint';
    const { $theme } = useNuxtApp();
    const { t } = useTranslation();

    const props = withDefaults(
        defineProps<
            FormInputProps & {
                validate?: boolean;
            }
        >(),
        {
            validate: false,
        }
    );
    const inputData: FormInputData = useFormInput(props);
    const focused = ref(false);

    const emailInput = ref(null);
    onClickOutside(emailInput, () => (focused.value = false));

    const endpoint = new ValidateEmailEndpoint();
    const response = ref<ValidatedEmailResponse | null>(null);
    const validating = ref(false);
    const { abortable, abort } = useAbortableFetch();

    const hasClientFormat = computed(() => {
        const regex = /\S+@\S+\.\S+/;
        return regex.test(inputData.model.value);
    });

    const clearFormError = () => {
        if (inputData.form.errors?.has(inputData.inputName)) {
            inputData.form.errors.clear(inputData.inputName);
        }
    };

    const runValidation = async () => {
        abort();
        clearFormError();

        if (!inputData.model.value) {
            response.value = null;
            validating.value = false;
            return;
        }

        if (!hasClientFormat.value) {
            response.value = null;
            validating.value = false;
            inputData.form.errors.add(inputData.inputName, [t('misc.email_input.invalid_format')]);
            return;
        }

        validating.value = true;
        try {
            response.value = await endpoint.withAbortController(abortable.value).validate(inputData.model.value);
            if (!response.value.valid && response.value.reason) {
                inputData.form.errors.add(inputData.inputName, [t(`misc.email_input.${response.value.reason}`)]);
            }
        } catch (error) {
            console.error('Email validation request failed', error);
            response.value = null;
        }
        validating.value = false;
    };

    // Validate as you type (debounced).
    const debouncedValidate = useDebounceFn(runValidation, 400);

    const onInput = () => {
        if (!props.validate) return;
        response.value = null;
        abort();
        debouncedValidate();
    };

    const onBlur = () => {
        focused.value = false;
    };

    // Picking a suggestion sets the value programmatically (no input/blur event) — validate directly.
    const selectSuggestion = (suggestion: string) => {
        inputData.model.value = suggestion;
        if (props.validate) runValidation();
    };

    const isValid = computed(() => {
        if (!inputData.model.value) return false;
        if (response.value === null) return hasClientFormat.value;
        return response.value.valid;
    });

    const submittable = computed(() => response.value?.valid === true);

    defineExpose({ submittable });

    onBeforeUnmount(() => abort());

    const suggestions = computed<string[]>(() => {
        if (!focused.value || !inputData.model.value || inputData.model.value.length < 3) {
            return [];
        }

        const providers = ['gmail.com', 'hotmail.com', 'icloud.com', 'outlook.com', 'yahoo.com', 'yahoo.fr', 'orange.fr', 'hotmail.fr'];

        // TODO: Customize the providers depending on the tenant's country.
        // if ( tenant.billingCountry == 'fr' ) {
        //     providers.unshift('free.fr');
        // }

        const all = providers.map((provider) => Str(inputData.model.value).before('@').append(`@${provider}`).get());

        return all.filter((email) => email.startsWith(inputData.model.value) && email !== inputData.model.value);
    });
</script>

<template>
    <FormElement :input-data="inputData">
        <div
            ref="emailInput"
            class="relative"
        >
            <input
                :id="inputData.inputId.value"
                v-model="inputData.model.value"
                :class="[inputData.className.value, { 'border-red-500': !isValid && inputData.model.value.length > 0 && !validating }]"
                :name="inputData.props.inputName"
                :placeholder="inputData.placeholder.value"
                type="email"
                :disabled="inputData.props.disabled"
                @focus="focused = true"
                @input="onInput"
                @blur="onBlur"
            />
            <div
                v-if="suggestions.length > 0"
                :class="$theme('submenu.container.default', 'w-full')"
            >
                <div :class="$theme('submenu.items')">
                    <a
                        v-for="suggestion in suggestions"
                        :key="suggestion"
                        href="#"
                        :class="$theme('submenu.item')"
                        @mousedown.prevent="selectSuggestion(suggestion)"
                    >
                        {{ suggestion }}
                    </a>
                </div>
            </div>
            <template v-if="props.validate && inputData.model.value">
                <span
                    v-if="validating"
                    class="text-[11px] text-gray-400"
                >
                    <i class="fa-duotone fa-loader fa-spin" />
                    {{ $t('misc.email_input.validating') }}
                </span>
                <span
                    v-else-if="response?.valid"
                    class="text-[11px] text-green-600"
                >
                    <i class="fa-regular fa-check" />
                    {{ $t('misc.email_input.valid') }}
                </span>
            </template>
        </div>
    </FormElement>
</template>
